import type { QueryClient } from "@tanstack/react-query";
import type { LoaderFunction } from "react-router";

import { orgStorage } from "@/shared/auth";
import {
  ORG_DEFAULT_VIEWS_KEY,
  type OrgDefaultViewsConfig,
} from "@/shared/domain/config/ConfigRecords";
import { ConfigService } from "@/shared/domain/config/ConfigService";
import { getQueryKey as orgConfigQueryKey } from "@/shared/domain/config/hooks/useOrgConfig";
import { FeatureFlagService } from "@/shared/domain/featureFlags";
import { featureFlagQueryKeys } from "@/shared/domain/featureFlags/hooks/featureFlagQueryKeys";
import type { QueryTarget } from "@/shared/domain/query";
import { makeAuthenticatedHttpClient } from "@/shared/http/makeAuthenticatedHttpClient";
import { ErrorMonitoringService } from "@/shared/services";

import { viewQueryKeys } from "../hooks/viewQueryKeys";
import type { ViewRecord } from "../ViewRecord";
import { VIEW_SEARCH_PARAM } from "../viewSearchParam";
import { ViewService } from "../ViewService";

/** Reads a single View. Injected so the branch tests need no network stack. */
export type FetchView = (viewId: string, orgId: string) => Promise<ViewRecord>;

/** Reads every View on a target. Injected for the same reason. */
export type FetchViews = (
  target: QueryTarget,
  orgId: string,
) => Promise<ViewRecord[]>;

/**
 * Reads the flag matrix, naming `orgId` so it is included even for a caller who
 * can access that org without belonging to it. Injected so a test can hold it
 * pending or fail it.
 */
export type FetchFlagStates = (
  orgId: string,
) => Promise<Record<string, Record<string, boolean>>>;

/** Reads the org's default Views setting; `null` when it has never set one. */
export type FetchOrgDefaultViews = (
  orgId: string,
) => Promise<OrgDefaultViewsConfig | null>;

async function fetchOrgDefaultViewsOverHttp(
  orgId: string,
): Promise<OrgDefaultViewsConfig | null> {
  const httpClient = await makeAuthenticatedHttpClient();
  return new ConfigService(httpClient).getOrgConfig<OrgDefaultViewsConfig>(
    orgId,
    ORG_DEFAULT_VIEWS_KEY,
  );
}

async function fetchViewOverHttp(
  viewId: string,
  orgId: string,
): Promise<ViewRecord> {
  const httpClient = await makeAuthenticatedHttpClient();
  return new ViewService(httpClient).getViewById(viewId, orgId);
}

async function fetchViewsOverHttp(
  target: QueryTarget,
  orgId: string,
): Promise<ViewRecord[]> {
  const httpClient = await makeAuthenticatedHttpClient();
  return new ViewService(httpClient).getAllViews(target, orgId);
}

async function fetchFlagStatesOverHttp(
  orgId: string,
): Promise<Record<string, Record<string, boolean>>> {
  const httpClient = await makeAuthenticatedHttpClient();
  return new FeatureFlagService(httpClient).checkAllFeatureFlags({ orgId });
}

export interface ViewsLoaderOptions {
  /** Resource type this list shows, and so whose Views the picker offers. */
  target: QueryTarget;
  /** Override the single-View read for tests. */
  fetchView?: FetchView;
  /** Override the list read for tests. */
  fetchViews?: FetchViews;
  /** Override the flag read for tests. */
  fetchFlagStates?: FetchFlagStates;
  /** Override the org default read for tests. */
  fetchOrgDefaultViews?: FetchOrgDefaultViews;
}

/**
 * Give a resource list what its View surface needs *before* it paints.
 *
 * Two late arrivals used to move the page, and this closes both by making the
 * answers available on the first render rather than a beat into it:
 *
 *  1. **The picker button.** `ViewPicker` renders `null` until `release-views`
 *     resolves, and `useCheckFeatureFlag` reads a pending query as `false` —
 *     so the button and its separator used to appear a moment after the toolbar
 *     did, shifting the whole button row. `AuthProvider` starts this fetch but
 *     deliberately does not await it, so on a cold load the list can paint
 *     first. Awaiting it here costs only the wait on a request already in
 *     flight: same key, `staleTime: Infinity`, so TanStack dedupes onto it.
 *  2. **The linked View.** With `?view=` in the URL, resolving the record here
 *     lets `useViewFromLink` build the table filtered on its first render,
 *     rather than fetching an unfiltered page and replacing it.
 *
 * The picker's list is started here too, so the panel opens populated, but
 * not awaited: nothing on the first paint needs it, and a slow list read must
 * not delay the resources the route exists to show. The org's default Views
 * setting is started on the same terms: a member opening the table on their
 * org's default waits on it, the list itself does not.
 *
 * Always resolves `null`, and never blocks the list on a View failing to load.
 * A View is decoration on a resource list, never the reason the route exists:
 *
 *   - **No current org** — defers to the auth layout, as
 *     `makeFeatureFlagRedirectLoader` does.
 *   - **`release-views` off, or unreadable** — returns without asking for any
 *     View. Every View endpoint 403s while the flag is off, and no View UI
 *     mounts, so there is nothing to warm. Fails closed, matching the route
 *     gate and `useCheckFeatureFlag`.
 *   - **The View read failing** — swallowed. The list renders, and
 *     `useViewDeepLink` re-reads the linked View's query and turns 401 and 404
 *     into the two messages a recipient needs. Throwing would replace a whole
 *     list with an error boundary over a stale parameter. A failed list
 *     prefetch costs nothing either: `useViews` fetches on mount.
 */
export function makeViewsLoader(
  queryClient: QueryClient,
  {
    target,
    fetchView = fetchViewOverHttp,
    fetchViews = fetchViewsOverHttp,
    fetchFlagStates = fetchFlagStatesOverHttp,
    fetchOrgDefaultViews = fetchOrgDefaultViewsOverHttp,
  }: ViewsLoaderOptions,
): LoaderFunction {
  return async function loadViews({ request }) {
    const orgRecord = orgStorage.getCurrentOrg();
    if (!orgRecord) {
      return null;
    }
    const orgId = orgRecord.org_id;

    let flagStates: Record<string, Record<string, boolean>>;
    try {
      // Same key and `staleTime` as `AuthProvider`'s un-awaited prefetch and as
      // `useCheckFeatureFlag`, so this joins that request rather than making a
      // second one — dedupe is by key, not by function.
      flagStates = await queryClient.fetchQuery({
        queryKey: featureFlagQueryKeys.states(),
        queryFn: () => fetchFlagStates(orgId),
        staleTime: Infinity,
      });
    } catch {
      return null;
    }

    if (!flagStates[orgId]?.["release-views"]) {
      return null;
    }

    const viewId = new URL(request.url).searchParams.get(VIEW_SEARCH_PARAM);

    // Not awaited: the page does not wait on the list. `prefetchQuery`
    // swallows the query's failure, so it is reported from inside `queryFn`.
    void queryClient
      .prefetchQuery({
        queryKey: viewQueryKeys.list(orgId, target),
        queryFn: () =>
          fetchViews(target, orgId).catch((error: unknown) => {
            ErrorMonitoringService.captureError(error, {
              context: "viewsLoader",
              target,
            });
            throw error;
          }),
      })
      .catch(() => undefined);

    // Same key `useOrgConfig` reads, so a table opening on its org default finds
    // this request in flight, or already answered, rather than starting its own.
    void queryClient
      .prefetchQuery({
        queryKey: orgConfigQueryKey(orgId, ORG_DEFAULT_VIEWS_KEY),
        queryFn: () => fetchOrgDefaultViews(orgId),
      })
      .catch(() => undefined);

    if (viewId !== null) {
      await queryClient
        .ensureQueryData({
          queryKey: viewQueryKeys.id(orgId, viewId),
          queryFn: () => fetchView(viewId, orgId),
        })
        .catch(() => null);
    }

    return null;
  };
}
